<template>
	<div
		ref="captionsControl"
		class="captions-control"
		:class="{ '-dark': darkTheme }"
		@focusout="handleFocusOut"
	>
		<button
			ref="captionsButton"
			type="button"
			class="captions-control__button"
			:aria-label="$I18n.trans('view_mode.captions')"
			aria-haspopup="dialog"
			:aria-controls="menuId"
			:aria-expanded="showMenu ? 'true' : 'false'"
			:aria-pressed="captionsEnabled ? 'true' : 'false'"
			@click.stop="toggleMenu"
			@keydown.esc.stop.prevent="closeMenu"
		>
			<Icon
				icon-name="captions"
				:size="controlSize"
			/>
		</button>
		<div
			:id="menuId"
			class="captions-control__menu"
			:class="{ 'is-visible': showMenu, 'is-mobile': isMobileWebApp }"
			role="dialog"
			:aria-label="$I18n.trans('view_mode.captions')"
			@keydown.esc.stop.prevent="closeMenu"
			@click.self.stop="closeMenu"
		>
			<div
				v-if="showMenu"
				class="captions-control__options"
				role="group"
				:aria-label="$I18n.trans('view_mode.captions')"
			>
				<button
					type="button"
					class="captions-control__option"
					:aria-pressed="!captionsEnabled ? 'true' : 'false'"
					@click.stop="selectTrack(-1)"
				>
					<span
						class="captions-control__check"
						aria-hidden="true"
					>{{ !captionsEnabled ? '✓' : '' }}</span>
					<span>{{ $I18n.trans('view_mode.off') }}</span>
				</button>
				<button
					v-for="subtitleTrack in subtitleTracks"
					:key="subtitleTrack.id"
					type="button"
					class="captions-control__option"
					:aria-pressed="isSelected(subtitleTrack.id) ? 'true' : 'false'"
					@click.stop="selectTrack(subtitleTrack.id)"
				>
					<span
						class="captions-control__check"
						aria-hidden="true"
					>{{ isSelected(subtitleTrack.id) ? '✓' : '' }}</span>
					<span>{{ subtitleTrack.label }}</span>
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import Icon from '@shared/components/Icon.vue';

export default {
	name:       'CaptionsButton',
	components: { Icon },
	props:      {
		subtitleTracks: {
			type:    Array,
			default: () => [],
		},
		currentSubtitleTrackId: {
			type:    Number,
			default: -1,
		},
		controlSize: {
			type:     String,
			required: true,
		},
		isMobileWebApp: {
			type:    Boolean,
			default: false,
		},
		darkTheme: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			showMenu:          false,
			menuId:            `player-captions-menu-${this._uid}`,
			stillOpenInterval: null,
		};
	},
	computed: {
		captionsEnabled() {
			return this.currentSubtitleTrackId !== -1;
		},
	},
	mounted() {
		document.addEventListener('click', this.handleDocumentClick, { capture: true });
	},
	beforeDestroy() {
		document.removeEventListener('click', this.handleDocumentClick, { capture: true });
		clearInterval(this.stillOpenInterval);
	},
	methods: {
		toggleMenu() {
			if (this.showMenu) {
				this.closeMenu();
				return;
			}
			this.showMenu = true;
			this.$emit('opened');
			this.stillOpenInterval = setInterval(() => this.$emit('still-open'), 2000);
		},
		selectTrack(id) {
			this.$emit('set-subtitle-track', id);
			this.closeMenu();
		},
		isSelected(id) {
			return Number(id) === this.currentSubtitleTrackId;
		},
		closeMenu(returnFocus = true) {
			if (!this.showMenu) return;
			this.showMenu = false;
			clearInterval(this.stillOpenInterval);
			this.stillOpenInterval = null;
			this.$emit('close');
			if (returnFocus) this.$nextTick(() => this.$refs.captionsButton?.focus());
		},
		handleFocusOut() {
			setTimeout(() => {
				if (this.showMenu && !this.$refs.captionsControl?.contains(document.activeElement)) this.closeMenu(false);
			}, 0);
		},
		handleDocumentClick(event) {
			if (this.showMenu && !this.$refs.captionsControl?.contains(event.target)) this.closeMenu(false);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.captions-control {
	position: relative;
	display: flex;
	color: var(--color-white);

	&.-dark {
		color: var(--color-black);
	}
}

.captions-control__button {
	display: flex;
	color: inherit;
	line-height: 1;
	cursor: pointer;
}

.captions-control__menu {
	position: absolute;
	right: -16px;
	bottom: 32px;
	z-index: 2;
	display: flex;
	visibility: hidden;
	opacity: 0;
	transition: visibility 0.2s, opacity 0.2s ease-in-out;

	&.is-visible {
		visibility: visible;
		opacity: 1;
	}

	&.is-mobile {
		position: fixed;
		top: 0;
		right: auto;
		bottom: auto;
		left: 0;
		width: 100%;
		height: 100%;
		background-color: rgba(0, 0, 0, 0.6);
	}
}

.captions-control__options {
	display: flex;
	min-width: 180px;
	flex-direction: column;
	overflow: hidden;
	border-radius: $border-radius--md;
	background-color: var(--color-grey-850);
	color: var(--color-soft-white);

	body.-t-light & { // stylelint-disable-line selector-no-qualifying-type, selector-max-type
		background-color: var(--color-white);
		color: var(--color-black);
	}

	.captions-control__menu.is-mobile & {
		position: fixed;
		bottom: 0;
		left: 0;
		width: 100%;
		border-radius: var(--t-border-radius--xl) var(--t-border-radius--xl) 0 0;
	}
}

.captions-control__option {
	@include font(primary, medium, normal, fs-100);

	display: flex;
	align-items: center;
	padding: 12px 16px;
	color: inherit;
	white-space: nowrap;
	cursor: pointer;
}

.captions-control__check {
	display: inline-block;
	width: 20px;
	margin-right: 8px;
	color: var(--color-hype-yellow);
}
</style>
